// 사진 크게 보기의 확대·이동 계산과 시각 표시. 차량 사진과 휴양소 사진이 같이 쓴다.
// 화면과 무관한 순수 함수라 단위 테스트로 검증한다.

// ---------------------------------------------------------------------------
// 확대·이동. 사진은 영역 가운데를 기준으로 translate(x, y) scale(scale) 로 그린다.
// 영역 가운데에서 d 만큼 떨어진 사진 위의 점은 화면에서 (x + scale * d) 에 보인다.
// ---------------------------------------------------------------------------

export interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export const ZOOM_IDENTITY: ZoomState = { scale: 1, x: 0, y: 0 };
export const TAP_ZOOM_SCALE = 2;
export const MAX_ZOOM_SCALE = 4;

/**
 * 확대한 사진이 밀려나 빈 화면이 드러나지 않도록 이동 범위를 막는다.
 * 사진은 비율을 지켜 영역 안에 맞춰 그리므로(contain) 영역보다 좁거나 낮을 수 있다.
 * 그래서 한계는 확대된 "사진" 크기가 영역을 넘는 만큼만 허용한다. 넘지 않으면 가운데에 둔다.
 * @param size 영역 크기
 * @param content 확대 전 사진이 영역 안에서 차지하는 크기. 생략하면 영역과 같다고 본다
 */
export function clampPan(state: ZoomState, size: Size, content: Size = size): ZoomState {
  const maxX = Math.max(0, (state.scale * content.width - size.width) / 2);
  const maxY = Math.max(0, (state.scale * content.height - size.height) / 2);
  return {
    scale: state.scale,
    x: Math.max(-maxX, Math.min(maxX, state.x)) || 0,
    y: Math.max(-maxY, Math.min(maxY, state.y)) || 0,
  };
}

/**
 * 사진을 한 번 눌렀을 때. 원래 크기면 누른 지점이 그 자리에 머물도록 2배 확대하고,
 * 이미 확대돼 있으면 원래 크기로 돌린다.
 * @param point 영역 가운데를 (0, 0) 으로 한 누른 위치
 */
export function tapZoom(
  state: ZoomState,
  point: { x: number; y: number },
  size: Size,
  content: Size = size
): ZoomState {
  if (state.scale > 1) return ZOOM_IDENTITY;
  const scale = TAP_ZOOM_SCALE;
  // 누른 점 p 가 가리키는 사진 위의 점 d = p (원래 크기). 확대 후에도 p 에 보이려면 x + scale * p = p
  return clampPan({ scale, x: point.x * (1 - scale), y: point.y * (1 - scale) }, size, content);
}

/** 두 손가락 확대. 시작 때의 배율에 손가락 사이 거리 비율을 곱한다. */
export function pinchZoom(
  start: ZoomState,
  startDistance: number,
  distance: number,
  size: Size,
  content: Size = size
): ZoomState {
  if (startDistance <= 0) return start;
  const scale = Math.max(1, Math.min(MAX_ZOOM_SCALE, (start.scale * distance) / startDistance));
  if (scale === 1) return ZOOM_IDENTITY;
  const ratio = scale / start.scale;
  return clampPan({ scale, x: start.x * ratio, y: start.y * ratio }, size, content);
}

/** "2026-10-01T10:26:13.123" → "2026-10-01 10:26" */
export function formatDateTime(value?: string | null): string {
  if (!value) return "";
  return value.replace("T", " ").slice(0, 16);
}

/** "2026-10-01T10:26:13" → "10:26" */
export function formatTime(value?: string | null): string {
  return formatDateTime(value).slice(11, 16);
}

/** 목록에서 delta 만큼 넘긴 위치. 끝과 처음이 이어진다. */
export function stepIndex(index: number, delta: number, length: number): number {
  if (length <= 0) return 0;
  return (index + delta + length) % length;
}
